import { useCanOpenRoom } from "../../domains/room/hooks/useCanOpenRoom";
import { useEffectOnce } from "../../domains/app/hooks/useEffectOnce";
import { usePlaylistPanelContent } from "../../domains/playlist/hooks/usePlaylistPanelContent";
import { useSaveVideo } from "../../domains/user/hooks/useSaveVideo";
import { useShareVideo } from "../../domains/user/hooks/useShareVideo";
import { isUserAuthentified } from "../../domains/user/utils/isUserAuthentified";
import { useCongressAttributionFromNavigation } from "../../domains/congress/congressAttribution";
import { apiClient, useApiQuery, type Api } from "../../services/api";
import { AnalyticsItemList } from "../../tools/analytics/analytics.types";
import { gaEventPageView } from "../../tools/analytics/common.analytics";
import { gaEventViewItemListArticle } from "../../tools/analytics/domains/article.analytics";
import { gaEventViewItemVideo } from "../../tools/analytics/domains/video.analytics";

type VideoDetail = Extract<
  Api["get"]["/webapp/public/content/:id"]["output"]["body"],
  { content_format: "Video" }
>;

export function formatVideoDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s.toString().padStart(2, "0")}s`;
}

/**
 * What a video page reads, on the phone and on the desktop: the video, its
 * room, the publications recommended with it, the save and share flows, and
 * the page view and list view it reports once loaded.
 */
// The explicit return type keeps the declaration portable (TS2883 on ContentfulStatusCode).
export function useVideoPage(props: { slug: string; roomId: string | null }): {
  contentQuery: ReturnType<
    typeof useApiQuery<"/webapp/public/content/slug/:slug">
  >;
  video: VideoDetail | null;
  currentRoom: Api["get"]["/webapp/private/rooms/:id"]["output"]["body"] | null;
  relatedArticles:
    | Api["get"]["/webapp/public/content/:id/related-articles"]["output"]["body"]
    | null;
  isSaved: ReturnType<typeof useSaveVideo>["isSaved"];
  toggleSaveToPlaylists: ReturnType<
    typeof useSaveVideo
  >["toggleSaveToPlaylists"];
  shareVideo: ReturnType<typeof useShareVideo>["triggerShare"];
  playlistPanel: ReturnType<typeof usePlaylistPanelContent>;
} {
  // The row only dresses the page (logo, analytics). A shared link brings
  // readers the row refuses (no session, or not in a private room), and the
  // refusal would only reach Sentry: ask only when the reader can open it.
  const canOpenRoom = useCanOpenRoom(props.roomId);
  const roomQuery = useApiQuery(
    "/webapp/private/rooms/:id",
    props.roomId !== null && canOpenRoom === "open"
      ? { param: { id: props.roomId } }
      : false
  );
  const currentRoom = roomQuery.state === "success" ? roomQuery.body : null;
  // Stamped by the card that navigated here; null on every other entry.
  const congressAttribution = useCongressAttributionFromNavigation();

  const contentQuery = useApiQuery(
    "/webapp/public/content/slug/:slug",
    props.slug.length > 0 ? { param: { slug: props.slug } } : false
  );
  const video: VideoDetail | null =
    contentQuery.state === "success" &&
    contentQuery.body.content_format === "Video"
      ? contentQuery.body
      : null;

  const relatedQuery = useApiQuery(
    "/webapp/public/content/:id/related-articles",
    video !== null ? { param: { id: video.id } } : false
  );
  const relatedArticles =
    relatedQuery.state === "success" ? relatedQuery.body : null;

  const { isSaved, toggleSaveToPlaylists } = useSaveVideo(
    video,
    AnalyticsItemList.VIDEO_ARTICLE_PAGE
  );
  const { triggerShare: shareVideo } = useShareVideo(
    AnalyticsItemList.VIDEO_ARTICLE_PAGE
  );
  const playlistPanel = usePlaylistPanelContent();

  useEffectOnce(() => {
    if (video === null) return;
    void (async () => {
      try {
        await gaEventPageView(currentRoom);
        if (await isUserAuthentified()) {
          await apiClient.post("/webapp/private/content/:id/views", {
            param: { id: video.id },
          });
        }
        gaEventViewItemVideo(
          {
            video,
            listName: AnalyticsItemList.VIDEO_ARTICLE_PAGE,
          },
          congressAttribution
        );
      } catch {
        // analytics-only, captured by onRequestError
      }
    })();
  }, [video === null ? null : video.id]);

  useEffectOnce(() => {
    if (relatedArticles === null || relatedArticles.length === 0) return;
    gaEventViewItemListArticle({
      articles: relatedArticles,
      listName: AnalyticsItemList.VIDEO_ARTICLE_PAGE,
    });
  }, [
    relatedArticles === null
      ? null
      : relatedArticles.map((a) => a.id).join(","),
  ]);

  return {
    contentQuery,
    video,
    currentRoom,
    relatedArticles,
    isSaved,
    toggleSaveToPlaylists,
    shareVideo,
    playlistPanel,
  };
}
